<template>
  <div id="app-wrapper">

    <TopNavigation v-if="showNavigation" />

    <div class="layout-main" :class="{ 'with-navigation': showNavigation }">
      <div v-if="isRouteLoading" class="route-loading-overlay">
        <ProgressSpinner />
      </div>

      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </div>

    <WhatsNewDialog
      v-model:visible="showWhatsNewPopup"
      :new-entries="newEntriesSinceLastSeen"
    />

    <InterfaceUnlockDialog v-model:visible="showInterfaceUnlockPopup" />

    <ConfirmDialog />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/modules/admin/stores/auth';
import { useTheme } from '@/composables/useTheme';
import { useChangelog } from '@/modules/admin/composables/useChangelog';
import { useInterfaceUnlock } from '@/composables/useInterfaceUnlock';
import { Urls } from '@/config/Urls';
import TopNavigation from '@/components/layout/TopNavigation.vue';
import WhatsNewDialog from '@/components/common/WhatsNewDialog/WhatsNewDialog.vue';
import InterfaceUnlockDialog from '@/components/common/InterfaceUnlockDialog/InterfaceUnlockDialog.vue';
import { ProgressSpinner, ConfirmDialog } from '@/components/primevue';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const { fetchChangelog, hasNewEntries, newEntriesSinceLastSeen } = useChangelog();

// Initialize theme system (reads localStorage, applies data-theme attribute)
useTheme();

// Track route loading state
const isRouteLoading = ref(false);
const showWhatsNewPopup = ref(false);
const changelogFetchedForSession = ref(false);
const showInterfaceUnlockPopup = ref(false);
const { fetchUnlockState, announcementPending } = useInterfaceUnlock();

// Public routes that don't show navigation
const publicRoutes = [
  Urls.LOGIN,
  // Urls.REGISTER,
  // Urls.FORGOT_PASSWORD,
  // Urls.FORGOT_PASSWORD_CONFIRMATION,
  // Urls.RESET_PASSWORD,
  // Urls.ACCOUNT_VERIFICATION,
  // Urls.ACCOUNT_VERIFICATION_EMAIL,
];

const showNavigation = computed(() => {
  const isPublicRoute = publicRoutes.some((publicRoute) => route.path.startsWith(publicRoute));
  return authStore.isAuthenticated && !isPublicRoute;
});

watch(
  showNavigation,
  async (show) => {
    if (!show || changelogFetchedForSession.value) return;
    changelogFetchedForSession.value = true;
    await fetchChangelog();
    if (hasNewEntries.value) {
      showWhatsNewPopup.value = true;
    }
    // Trivium Lite (XP-18): server tells us whether the one-off unlock
    // announcement is still pending for this user. Queued behind the
    // What's New popup so the two dialogs never stack.
    await fetchUnlockState();
    maybeShowInterfaceUnlockPopup();
  },
  { immediate: true }
);

// If What's New was open, surface the unlock announcement after it closes.
watch(showWhatsNewPopup, (open) => {
  if (!open) maybeShowInterfaceUnlockPopup();
});

function maybeShowInterfaceUnlockPopup() {
  if (announcementPending.value && !showWhatsNewPopup.value) {
    showInterfaceUnlockPopup.value = true;
  }
}

// Watch for route changes to show loading
let loadingTimeout: ReturnType<typeof setTimeout> | null = null;

router.beforeEach((to, from, next) => {
  if (to.path !== from.path) {
    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
      loadingTimeout = null;
    }
    isRouteLoading.value = true;
  }
  next();
});

router.afterEach((_to, _from) => {
  if (!isRouteLoading.value) return;
  loadingTimeout = setTimeout(() => {
    isRouteLoading.value = false;
    loadingTimeout = null;
  }, 150);
});

router.onError(() => {
  // Hide loading on error
  isRouteLoading.value = false;
  if (loadingTimeout) {
    clearTimeout(loadingTimeout);
    loadingTimeout = null;
  }
});
</script>

<style lang="scss">
body {
  overflow: hidden;
  margin: 0;
  padding: 0;
}

#app-wrapper {
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: background-color 0.3s ease, color 0.3s ease;
}

.layout-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  position: relative;

  &.with-navigation {
    margin-top: 60px;
  }
}

.route-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--overlay-loading);
  z-index: 9999;
  backdrop-filter: blur(2px);
}

// Fade transition for route changes
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

</style>
